import FilterDrawer from 'components/InstructorSubmissions/FilterDrawer';
import ConfirmPushGradesDialog from 'components/InstructorViewAssignment/ConfirmPushGradesDialog';
import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import Desktop from 'pages/InstructorSubmissions/desktop';
import Mobile from 'pages/InstructorSubmissions/mobile';
import { PageContext } from 'pages/InstructorSubmissions/PageProvider';
import Tablet from 'pages/InstructorSubmissions/tablet';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import { Icon, Loading, Stack, Text } from 'shell/components';

const PageLayout = () => {
    const { isLoading } = useApp();
    const history = useHistory();
    const { device } = useViewport();
    const { t } = useTranslation();

    const contextProps = useContext(PageContext);

    const {
        assignment,
        submissions,
        confirmPushGradesDialog,
        setConfirmPushGradesDialog,
        sortState,
        handleSetSortState,
        columns,
    } = contextProps;

    const getFilterCount = (filters, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filters.status.length) count++;
        return count;
    };

    const getSubmissionSection = (submission) => {
        return submission.api?.user?.sections
            ? submission.api?.user?.sections?.map(({ name }) => name)?.join(', ')
            : (submission.api?.user?.section?.name ?? '--');
    };

    const getSubmissionStatus = (submission) => {
        if (submission.grading) {
            return (
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon kind={'checkmark'} />
                    <Text>{t('status.reviewed', 'Reviewed')}</Text>
                </Stack>
            );
        }

        if (submission.ai) {
            return (
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon kind={'clock'} />
                    <Text>{t('status.ready-for-review', 'Ready for review')}</Text>
                </Stack>
            );
        }

        if (submission.is_failed) {
            return (
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon kind={'error'} />
                    <Text>{t('status.failed-to-process', 'Failed to process')}</Text>
                </Stack>
            );
        }

        return null;
    };

    const getSubmissionFinalGrade = (submission) => {
        if (!submission.grading || !assignment.ai_grading_enabled) return '--';
        const score =
            Boolean(submission.grade_overrides) && Object.keys(submission.grade_overrides).includes('total')
                ? submission.grade_overrides.total
                : Object.entries(submission.grading.criteria).reduce((acc, [criterionId, optionId]) => {
                      return Boolean(submission.grade_overrides) &&
                          Object.keys(submission.grade_overrides).includes(criterionId)
                          ? acc + Number(submission.grade_overrides[criterionId])
                          : acc +
                                Number(
                                    assignment.rubric.criteria
                                        .find(({ id }) => id === criterionId)
                                        .options.find(({ id }) => id === optionId).rate
                                );
                  }, 0);
        const maxScore = assignment.rubric.criteria.reduce((acc, criterion) => {
            return acc + Math.max(...criterion.options.map(({ rate }) => Number(rate)));
        }, 0);
        return `${score}/${maxScore}`;
    };

    const handleClickBack = () => {
        return history.push(`/assignments/${assignment.id}`);
    };

    const handleClickViewAll = () => {
        const targetSubmission = submissions.find((submission) => submission.grading);
        return history.push(`/assignments/${assignment.id}/submissions/${targetSubmission.id}`);
    };

    const handleClickViewSubmission = (submission) => {
        return history.push(`/assignments/${assignment.id}/submissions/${submission.id}`);
    };

    const props = {
        ...contextProps,
        getFilterCount,
        getSubmissionSection,
        getSubmissionStatus,
        getSubmissionFinalGrade,
        onClickBack: handleClickBack,
        onClickViewAll: handleClickViewAll,
        onClickViewSubmission: handleClickViewSubmission,
    };

    const renderResponsiveLayout = (device) => {
        if (!submissions.length) return <Redirect to={`/assignments/${assignment.id}`} />;

        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading || !submissions ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <ConfirmPushGradesDialog
                open={confirmPushGradesDialog.open}
                assignment={assignment}
                match={confirmPushGradesDialog.is_not_matching ? false : true}
                submissions={submissions?.filter(({ grading }) => grading)}
                onClose={() => setConfirmPushGradesDialog({ open: false })}
            />
            <FilterDrawer columns={columns} sortState={sortState} handleSetSortState={handleSetSortState} />
        </>
    );
};

export default PageLayout;
